import React, { useState, useEffect, useCallback } from 'react';
import { formatMoney } from '../../utils/format';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { projectService } from '../../services/projectService';
import { buildingTypeService } from '../../services/buildingTypeService';
import { functionalUnitService, FunctionalUnit } from '../../services/functionalUnitService';
import { ProjectFormData, WorkStatus, WorkStatusLabels, BuildingTypeWithoutWorkTypes } from '../../types';
import { ProjectStatusBadge } from '../../components/Projects/ProjectStatusBadge';
import {
  Plus,
  Building2,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
  Search,
  ArrowUpDown,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Ban,
} from 'lucide-react';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { showSuccess, showError } from '../../components/Alerts';
import { PERMISSIONS } from '../../constants/permissions';
import RequirePermission from '../../components/RequirePermission';

const ROWS_PER_PAGE = 12;

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectFormData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [workTypeFilter, setWorkTypeFilter] = useState('all');
  const [workStatusFilter, setWorkStatusFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<ProjectFormData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletingConfirm, setDeletingConfirm] = useState(false);
  const [projectUFs, setProjectUFs] = useState<FunctionalUnit[]>([]);
  const [buildingTypes, setBuildingTypes] = useState<BuildingTypeWithoutWorkTypes[]>([]);
  const [creationOrderFilter, setCreationOrderFilter] = useState('all');
  const [sortColumn, setSortColumn] = useState<'name' | 'location' | 'status' | 'buildingType' | 'cost' | 'currency' | 'createdAt' | 'updatedAt'>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await projectService.getAll();
      const data2 = await buildingTypeService.getAllBuildingTypesWithWorkTypes();

      setProjects(data);
      setBuildingTypes(data2.data);
    } catch (error) {
      console.error('Error loading projects:', error);
      showError('Error al cargar los proyectos');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const applyFilters = () => {
    return projects.filter(project => {
      const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.workCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${project.street} ${project.streetNumber}`.toLowerCase().includes(searchTerm.toLowerCase());

      // Filtro de tipo de obra
      let matchesWorkType = true;
      if (workTypeFilter && workTypeFilter !== 'all') {
        if (workTypeFilter === 'personalizados') {
          // Mostrar solo proyectos con tipos de obra personalizados (no predefinidos)
          const predefinedTypes = buildingTypes.map(bt => bt.value);
          matchesWorkType = !!project.buildingType && !predefinedTypes.includes(project.buildingType);
        } else {
          // Comparar por el value del buildingType, no por el id
          const projectBuildingType = buildingTypes.find(bt => bt.id.toString() === workTypeFilter);
          matchesWorkType = project.buildingType === projectBuildingType?.value;
        }
      }

      const matchesWorkStatus = !workStatusFilter || workStatusFilter === 'all' || project.projectStatus === workStatusFilter;

      return matchesSearch && matchesWorkType && matchesWorkStatus;
    });
  };

  const sortProjects = (filteredProjects: ProjectFormData[]) => {
    return filteredProjects.sort((a, b) => {
      let comparison = 0;

      // Si hay un filtro de orden de creación activo, usarlo
      if (creationOrderFilter !== 'all') {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return creationOrderFilter === 'oldest' ? dateA - dateB : dateB - dateA;
      }

      switch (sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'location':
          const locationA = `${a.street} ${a.streetNumber} ${a.city}`.toLowerCase();
          const locationB = `${b.street} ${b.streetNumber} ${b.city}`.toLowerCase();
          comparison = locationA.localeCompare(locationB);
          break;
        case 'status':
          comparison = (a.projectStatus || '').localeCompare(b.projectStatus || '');
          break;
        case 'buildingType':
          comparison = (a.buildingType || '').localeCompare(b.buildingType || '');
          break;
        case 'cost':
          comparison = (a.totalEstimatedCost || 0) - (b.totalEstimatedCost || 0);
          break;
        case 'currency':
          comparison = (a.currency || '').localeCompare(b.currency || '');
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
          break;
        case 'updatedAt':
          comparison = new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime();
          break;
        default:
          comparison = a.name.localeCompare(b.name);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const handleSort = (column: 'name' | 'location' | 'status' | 'buildingType' | 'cost' | 'currency' | 'createdAt' | 'updatedAt') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleDeleteProject = async (project: ProjectFormData) => {
    setProjectToDelete(project);
    setDeleteLoading(true);
    setProjectUFs([]);
    setDeleteDialogOpen(true);
    try {
      const ufList = await functionalUnitService.getAllFunctionalUnits(project.id!);
      setProjectUFs(ufList);
    } catch {
      // Si falla la carga de UFs, igual abrimos el dialog (el backend validará)
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    setDeletingConfirm(true);
    try {
      await projectService.deleteById(projectToDelete.id!);
      showSuccess('Proyecto eliminado exitosamente');
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      setProjectUFs([]);
      loadProjects();
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Error al eliminar el proyecto';
      showError(msg);
    } finally {
      setDeletingConfirm(false);
    }
  };

  const filteredAndSortedProjects = sortProjects(applyFilters());
  const totalPages = Math.ceil(filteredAndSortedProjects.length / ROWS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageProjects = filteredAndSortedProjects.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

  useEffect(() => { setPage(1); }, [searchTerm, workTypeFilter, workStatusFilter, creationOrderFilter]);

  if (loading) {
    return (
      <LoadingScreen message="Cargando proyectos" />
    );
  }

  return (
    <div className="w-full h-full p-6 space-y-6">
      {/* Card Container */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border">
          <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Lista de Proyectos</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Gestiona y visualiza información de todos los proyectos
              </p>
            </div>
            <div className="flex gap-2">
              <RequirePermission permission={PERMISSIONS.PROYECTOS.CREAR}>
                <Button
                  size="sm"
                  onClick={() => navigate('/projects/new')}
                  className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90"
                >
                  <Plus className="w-4 h-4" />
                  Nuevo Proyecto
                </Button>
              </RequirePermission>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col items-end gap-4 md:flex-row">
            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  Estado
                </label>
                <Select value={workStatusFilter} onValueChange={setWorkStatusFilter}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Todos los estados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    {Object.entries(WorkStatusLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  Tipo de Obra
                </label>
                <Select value={workTypeFilter} onValueChange={setWorkTypeFilter}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Todos los tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    {buildingTypes.map((bt) => (
                      <SelectItem key={bt.id} value={bt.id.toString()}>
                        {bt.value}
                      </SelectItem>
                    ))}
                    <SelectItem value="personalizados">Personalizados</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  Orden de Creación
                </label>
                <Select value={creationOrderFilter} onValueChange={setCreationOrderFilter}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Sin ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Sin ordenar</SelectItem>
                    <SelectItem value="newest">Más nuevo</SelectItem>
                    <SelectItem value="oldest">Más viejo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Search */}
            <div className="w-full space-y-2 md:w-72 md:ml-auto">
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar proyectos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {filteredAndSortedProjects.length === 0 ? (
            <div className="py-12 text-center">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-medium text-foreground">No hay proyectos</h3>
              <p className="mb-4 text-sm text-muted-foreground">Comienza creando el primer proyecto</p>
              <RequirePermission permission={PERMISSIONS.PROYECTOS.CREAR}>
                <Button
                  onClick={() => navigate('/projects/new')}
                  className="bg-primary text-primary-foreground hover:opacity-90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Proyecto
                </Button>
              </RequirePermission>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                    >
                      Proyecto
                      <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'name' ? 'text-primary' : ''}`} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('location')}
                      className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                    >
                      Ubicación
                      <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'location' ? 'text-primary' : ''}`} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                    >
                      Estado
                      <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'status' ? 'text-primary' : ''}`} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('buildingType')}
                      className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                    >
                      Tipo de Obra
                      <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'buildingType' ? 'text-primary' : ''}`} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('cost')}
                      className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                    >
                      Costo Total
                      <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'cost' ? 'text-primary' : ''}`} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('currency')}
                      className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                    >
                      Moneda
                      <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'currency' ? 'text-primary' : ''}`} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('updatedAt')}
                      className="flex items-center gap-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
                    >
                      Última Actualización
                      <ArrowUpDown className={`w-4 h-4 ${sortColumn === 'updatedAt' ? 'text-primary' : ''}`} />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-sm font-medium text-center text-muted-foreground">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageProjects.map((project, index) => {
                  const isLast = index === pageProjects.length - 1;
                  const rowClasses = isLast ? "px-4 py-3" : "px-4 py-3 border-b border-border";

                  return (
                    <tr key={project.id} className="transition-colors hover:bg-muted/30">
                      {/* Proyecto */}
                      <td className={rowClasses}>
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 p-2 bg-indigo-100 rounded-lg">
                            <Building2 className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-foreground">
                              {project.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {project.client || 'Sin cliente'}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Ubicación */}
                      <td className={rowClasses}>
                        <div className="flex flex-col">
                          <span className="text-sm text-foreground">
                            {project.street} {project.streetNumber}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {project.city}
                          </span>
                        </div>
                      </td>

                      {/* Estado */}
                      <td className={rowClasses}>
                        <ProjectStatusBadge
                          status={project.projectStatus || WorkStatus.EN_CONSTRUCCION}
                          label={WorkStatusLabels[project.projectStatus || WorkStatus.EN_CONSTRUCCION]}
                          size="sm"
                        />
                      </td>

                      {/* Tipo de Obra */}
                      <td className={rowClasses}>
                        <span className="text-sm text-foreground">
                          {project.buildingType || 'No especificado'}
                        </span>
                      </td>

                      {/* Costo Total */}
                      <td className={rowClasses}>
                        <span className="text-sm font-semibold text-amount">
                          ${formatMoney(project.totalEstimatedCost) || 'N/A'}
                        </span>
                      </td>

                      {/* Moneda */}
                      <td className={rowClasses}>
                        <span className="text-sm font-medium text-foreground">
                          {project.currency || 'No definida'}
                        </span>
                      </td>

                      {/* Última Actualización */}
                      <td className={rowClasses}>
                        <div className="flex flex-col">
                          <span className="text-sm text-foreground">
                            {project.updatedAt ? new Date(project.updatedAt).toLocaleDateString('es-AR') : 'N/A'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {project.updatedAt ? new Date(project.updatedAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      </td>

                      {/* Acciones */}
                      <td className={`${rowClasses} text-center`}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="inline-flex items-center justify-center p-2 transition-colors rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground"
                              title="Más acciones"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={() => navigate(`/projects/${project.id}`)}
                              className="cursor-pointer"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Ver proyecto
                            </DropdownMenuItem>
                            <RequirePermission permission={PERMISSIONS.PROYECTOS.EDITAR}>
                              <DropdownMenuItem
                                onClick={() => navigate(`/projects/${project.id}/edit`)}
                                className="cursor-pointer"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Editar proyecto
                              </DropdownMenuItem>
                            </RequirePermission>
                            <RequirePermission permission={PERMISSIONS.PROYECTOS.ELIMINAR}>
                              <DropdownMenuItem
                                onClick={() => handleDeleteProject(project)}
                                className="cursor-pointer text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar proyecto
                              </DropdownMenuItem>
                            </RequirePermission>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {filteredAndSortedProjects.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Mostrando {pageProjects.length} de {filteredAndSortedProjects.length} proyectos
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        if (!open) { setProjectToDelete(null); setProjectUFs([]); }
        setDeleteDialogOpen(open);
      }}>
        <DialogContent className="rounded-lg bg-card border-border sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Eliminar Proyecto
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Proyecto: <span className="font-medium text-foreground">"{projectToDelete?.name}"</span>
            </DialogDescription>
          </DialogHeader>

          {deleteLoading ? (
            <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Verificando unidades funcionales...</span>
            </div>
          ) : (() => {
            const soldUFs = projectUFs.filter(u => u.isSale);
            const unsoldUFs = projectUFs.filter(u => !u.isSale);

            if (soldUFs.length > 0) {
              return (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 p-3 text-sm rounded-md bg-destructive/10 text-destructive">
                    <Ban className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Este proyecto tiene <strong>{soldUFs.length} unidad{soldUFs.length > 1 ? 'es funcionales vendidas' : ' funcional vendida'}</strong>.
                      No puede eliminarse porque hay ventas concretadas registradas.
                    </span>
                  </div>
                  <div className="max-h-40 overflow-y-auto rounded border border-border divide-y divide-border">
                    {soldUFs.map(u => (
                      <div key={u.id} className="px-3 py-2 text-xs flex items-center gap-2 text-foreground">
                        <span className="font-medium">Piso {u.floor} – {u.letter}</span>
                        <span className="text-muted-foreground">{u.ufType?.name}</span>
                        <span className="ml-auto text-destructive font-semibold">VENDIDA</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setProjectToDelete(null); setProjectUFs([]); }}>
                      Entendido
                    </Button>
                  </div>
                </div>
              );
            }

            if (unsoldUFs.length > 0) {
              return (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 p-3 text-sm rounded-md bg-amber-50 text-amber-800 border border-amber-200">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Al eliminar este proyecto también se eliminarán <strong>{unsoldUFs.length} unidad{unsoldUFs.length > 1 ? 'es funcionales' : ' funcional'}</strong>.
                      Esta acción es irreversible.
                    </span>
                  </div>
                  <div className="max-h-48 overflow-y-auto rounded border border-border divide-y divide-border">
                    {unsoldUFs.map(u => (
                      <div key={u.id} className="px-3 py-2 text-xs flex items-center gap-2 text-foreground">
                        <span className="font-medium">Piso {u.floor} – {u.letter}</span>
                        {u.ufType && <span className="text-muted-foreground">{u.ufType.name}</span>}
                        {u.ufLocation && <span className="text-muted-foreground">· {u.ufLocation.name}</span>}
                        <span className="ml-auto text-muted-foreground">
                          {(u.coveredArea + u.semiCoveredArea + u.freeArea + u.storageArea).toFixed(1)} m²
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setProjectToDelete(null); setProjectUFs([]); }}>
                      Cancelar
                    </Button>
                    <Button
                      onClick={confirmDeleteProject}
                      disabled={deletingConfirm}
                      className="rounded-lg bg-destructive text-destructive-foreground hover:opacity-90"
                    >
                      {deletingConfirm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                      Eliminar de todas formas
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  ¿Estás seguro de que querés eliminar el proyecto <span className="font-medium text-foreground">"{projectToDelete?.name}"</span>?
                  Esta acción no se puede deshacer.
                </p>
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setProjectToDelete(null); setProjectUFs([]); }}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={confirmDeleteProject}
                    disabled={deletingConfirm}
                    className="rounded-lg bg-destructive text-destructive-foreground hover:opacity-90"
                  >
                    {deletingConfirm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Eliminar
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects; 